import type { Web3TxMultiCalcSWR } from "@/hooks/tx-flow/useDepositCalculate";
import type { Permit2Actions } from "@/hooks/web3/usePermit2ApproveCallback";
import type { SupWeb3Balance, SupWeb3TxSingleVault } from "@/types";
import React, { type Dispatch, type SetStateAction, useMemo, useState } from "react";

import Web3DepositApprovalApprovePermit2 from "@/components/Web3/Deposit/Approval/ApprovePermit2";
import Web3DepositApprovalPermit2 from "@/components/Web3/Deposit/Approval/Permit2";
import useERC20Allowance from "@/hooks/web3/useERC20Allowance";
import { PERMIT2_ADDRESS } from "@uniswap/permit2-sdk";

export interface Permit2SignaturePerDestination {
  signature?: Permit2Actions;
  chainId: number;
}

export function reduceVaultListToPermitChains(vaultList: SupWeb3TxSingleVault[]): Permit2SignaturePerDestination[] {
  return vaultList.reduce<Permit2SignaturePerDestination[]>(
    (acc, singleVault) => (acc.some(item => item.chainId == singleVault.chain.id) ? acc : [...acc, { chainId: singleVault.chain.id as number, signature: undefined }]),
    []
  );
}

interface Web3DepositApprovalPermit2WrapperProps {
  calculatedRoutes: Web3TxMultiCalcSWR;
  setApprovalRequired: Dispatch<SetStateAction<boolean>>;
  selectedBalance: SupWeb3Balance;
  putPermit2Signature: (chainId: number, signature: Permit2Actions | undefined) => void;
  permit2Signature: Permit2SignaturePerDestination[];
  setPauseRequests: Dispatch<SetStateAction<boolean>>;
  disabled?: boolean;
}

export default function Web3DepositApprovalPermit2Wrapper({
  calculatedRoutes,
  setApprovalRequired,
  selectedBalance,
  putPermit2Signature,
  permit2Signature,
  setPauseRequests,
  disabled = false,
}: Web3DepositApprovalPermit2WrapperProps) {
  const totalBigIntAmount = useMemo(
    () => calculatedRoutes.data?.reduce((acc, singleCalculate) => (singleCalculate.in.adjusted_amount ? acc + BigInt(`${singleCalculate.in.adjusted_amount}`) : acc), 0n) ?? 0n,
    [calculatedRoutes.data]
  );

  const [isUniswapContractApproved, setIsUniswapContractApproved] = useState<boolean>(false);

  const allowance = useERC20Allowance(true, selectedBalance.address, PERMIT2_ADDRESS, selectedBalance.chainId) as bigint | undefined;

  const contractApprovalRequired = useMemo(() => {
    if (isUniswapContractApproved) {
      return false;
    }
    return (allowance ?? 0n) < totalBigIntAmount;
  }, [allowance, totalBigIntAmount, isUniswapContractApproved]);

  const { total, step, permitProps } = useMemo(() => {
    const unsignedPermits = permit2Signature.filter(item => !item.signature);
    const currentPermit = unsignedPermits.length > 0 ? unsignedPermits[0] : undefined;
    const rawReturn = {
      total: permit2Signature.length,
      step: permit2Signature.length - unsignedPermits.length + 1,
      permitProps: undefined,
    };
    if (!currentPermit) {
      setApprovalRequired(false);
      return rawReturn;
    }
    const currentUnsignedAmount =
      calculatedRoutes.data
        ?.filter(singleCalculate => singleCalculate.in.to_chain_id == currentPermit.chainId)
        .reduce((acc, singleCalculate) => (singleCalculate.in.adjusted_amount ? acc + BigInt(`${singleCalculate.in.adjusted_amount}`) : acc), 0n) ?? 0n;

    return {
      ...rawReturn,
      permitProps: {
        amount: currentUnsignedAmount,
        chainId: currentPermit.chainId,
      },
    };
  }, [calculatedRoutes.data, permit2Signature, setApprovalRequired]);

  return contractApprovalRequired ? (
    <Web3DepositApprovalApprovePermit2
      setIsUniswapContractApproved={setIsUniswapContractApproved}
      selectedBalance={selectedBalance}
      setPauseRequests={setPauseRequests}
      disabled={disabled}
    />
  ) : (
    permitProps && (
      <Web3DepositApprovalPermit2
        key={`web3-deposit-approval-permit2-${permitProps.chainId}-${permitProps.amount}`}
        totalBigIntAmount={permitProps.amount}
        chainId={permitProps.chainId}
        onStart={() => setPauseRequests(true)}
        onSuccess={(chainId: number, signature: Permit2Actions) => {
          putPermit2Signature(chainId, signature);
          setPauseRequests(false);
        }}
        onError={(chainId: number, error: Error) => {
          console.error("Error signing Permit2", { error, step, total, chainId });
          setPauseRequests(false);
          putPermit2Signature(chainId, undefined);
          setPauseRequests(false);
        }}
        selectedBalance={selectedBalance}
        disabled={disabled}
        currentStep={step}
        allSteps={total}
      />
    )
  );
}
